import {useDispatch, useSelector} from "react-redux";
import {useTranslation} from "react-i18next";
import useToast from "../../../../util/useToast";
import {
  editorConfigSelector,
  rxHideCategoryEditor,
  rxShowHideDeleteConfirmationModal,
  rxShowHideDeleteWarningModal,
  rxUpdateCategories,
  selectAllCategories
} from "../../../../slices/categoriesSlice";
import {useCallback, useEffect, useState} from "react";
import {MODE_EDIT} from "../../../../codes";
import {
  createTransactionCategory,
  deleteTransactionCategory,
  updateTransactionCategory
} from "../../../../api/ApiTransactionCategories";
import {ICONS} from "../../../../common/IconPicker";
import {getOrDefault} from "../../../../util/util";
import {DEFAULT_COLOR} from "../../../../common/ColorBox";
import {selectAllTransactions} from "../../../../slices/transactionsSlice";
import {selectAllRules} from "../../../../slices/rulesSlice";
import useNetwork from "../../../../util/useNetwork";
import useValidator from "../../../../util/useValidator";

const useCategoryEditor = () => {
  const dispatch = useDispatch();

  const {t} = useTranslation("settings_categories");
  const {showToastSuccess} = useToast();
  const {handleNetworkError} = useNetwork();
  const validate = useValidator();

  const {
    show,
    mode,
    category,
    deleteConfirmationModal,
    deleteWarningModal,
    deleteWarningModalMessage
  } = useSelector(editorConfigSelector);

  const transactionCategories = useSelector(selectAllCategories);
  const allTransactions = useSelector(selectAllTransactions);
  const transactionRules = useSelector(selectAllRules);

  const [editedCategory, setEditedCategory] = useState({
    errors: {},
    name: "",
    groupName: "",
    description: "",
    icon: ""
  });

  const hideCategoryEditor = () => dispatch(rxHideCategoryEditor());

  useEffect(() => {
    if (!category || !show) {
      return;
    }

    setEditedCategory({
      errors: {},
      name: getOrDefault(category.name, ""),
      groupName: getOrDefault(category.groupName, ""),
      description: getOrDefault(category.description, ""),
      color: getOrDefault(category.color, DEFAULT_COLOR),
      icon: getOrDefault(category.icon, ICONS[Math.floor(Math.random() * ICONS.length)]),
      active: getOrDefault(category.active, true)
    });
  }, [show, category]);

  const getErrors = values => {
    const errors = {};

    if (!values.name.trim().length) {
      errors.name = "category_editor_error_name_must_be_filled";
    } else if (transactionCategories.find(a => a !== category && a.name.toLowerCase() === values.name.trim().toLowerCase())) {
      errors.name = "category_editor_error_name_already_taken";
    }

    if (!values.groupName.trim().length) {
      errors.groupName = "category_editor_error_group_name_must_be_filled";
    }

    return errors;
  };

  const submit = e => {
    e.preventDefault();

    const values = {
      name: editedCategory.name,
      groupName: editedCategory.groupName,
      description: editedCategory.description,
      color: editedCategory.color,
      icon: editedCategory.icon,
      active: editedCategory.active
    };

    return validate(getErrors(values))
      .then(() => {
        if (mode === MODE_EDIT) {
          return updateTransactionCategory(category.id, values);
        } else {
          return createTransactionCategory(values);
        }
      })
      .then(result => dispatch(rxUpdateCategories(result)))
      .then(() => {
        showToastSuccess(t(mode === MODE_EDIT ? "category_edited" : "category_added"));

        hideCategoryEditor();
      })
      .catch(error => {
        handleNetworkError(error, {
          400: err => setEditedCategory(prev => ({...prev, errors: err.response.data}))
        });
      });
  };

  const updateEditedCategory = field => event => {
    const values = {};

    switch (field) {
      case "color":
        values.color = event;
        break;
      case "active":
        values.active = event;
        break;
      case "icon":
        values.icon = event;
        break;
      default:
        values[field] = event.target.value;
        break;
    }

    setEditedCategory(prev => ({...prev, ...values}));
  };

  const showDeleteConfirmationModal = useCallback((() => {
    if (allTransactions.some(t => t.category === category.id)) {
      dispatch(rxShowHideDeleteWarningModal({show: true, message: t("modal_delete_warning_linked_transactions")}));
    } else if (transactionRules.some(r => r.category === category.id)) {
      dispatch(rxShowHideDeleteWarningModal({show: true, message: t("modal_delete_warning_linked_rules")}));
    } else {
      dispatch(rxShowHideDeleteConfirmationModal(true));
    }
  }), [t, allTransactions, transactionRules, category, dispatch]);

  const hideDeleteConfirmationModal = useCallback(() => dispatch(rxShowHideDeleteConfirmationModal(false)), [dispatch]);

  const hideDeleteWarningModal = useCallback((() => {
    dispatch(rxShowHideDeleteWarningModal({show: false}));

    return Promise.resolve();
  }), [dispatch]);

  const onDeleteCategory = () => {
    return deleteTransactionCategory(category.id)
      .then(result => {
        dispatch(rxUpdateCategories(result));
        dispatch(rxHideCategoryEditor());
      })
      .catch(handleNetworkError)
      .finally(hideDeleteConfirmationModal);
  };

  return {
    show,
    t,
    mode,
    hideCategoryEditor,
    editedCategory,
    updateEditedCategory,
    submit,
    deleteConfirmationModal,
    showDeleteConfirmationModal,
    hideDeleteConfirmationModal,
    onDeleteCategory,
    deleteWarningModal,
    deleteWarningModalMessage,
    hideDeleteWarningModal
  };
};

export default useCategoryEditor;
